import React, { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Stack, Text, RadioButton, Overlay, LoadingBalls } from '@moda/om';
import { reportError } from '../../../lib/reportError';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { tc } from '../../../lib/trackingContext';
import {
  ReturnMethod as TReturnMethod,
  useReturnSummary,
  useUpdateReturnSummaryMutation
} from '../../../hooks/useReturnSummary';
import { Box } from '../../Box';
import { ReturnMethod } from '../../ReturnInformation';
import { RequestReturnMethodGuestUserFeatures } from './RequestReturnMethodGuestUserFeatures';

import './RequestReturnMethod.scss';

export const RequestReturnMethod: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { showErrorModal } = useErrorModal();
  const { returnSummary } = useReturnSummary({ orderNumber });
  const [updateReturnSummary, { loading }] = useUpdateReturnSummaryMutation(orderNumber);

  const handleChange = useCallback(
    async (returnMethodId: number) => {
      if (returnSummary?.attributes.returnMethodId === returnMethodId) return;

      try {
        await updateReturnSummary({ returnMethodId });
      } catch (error) {
        reportError('REQUEST_RETURN_CHANGE_METHOD', error);
        showErrorModal();
      }
    },
    [updateReturnSummary, showErrorModal, returnSummary]
  );

  const trackChosenReturnMethod = useCallback(
    (returnMethod: string) => {
      if (returnMethod === 'Original Payment Method') {
        tc.track('Original Payment Refund Method Selected', {
          addToScopeOnce: { returnSummary }
        });
      }
      if (returnMethod === 'Site Credit') {
        tc.track('Site Credit Refund Method Selected', {
          addToScopeOnce: { returnSummary }
        });
      }
    },
    [returnSummary]
  );

  useEffect(() => {
    if (returnSummary && returnSummary.attributes.returnMethods.length > 1)
      tc.track('Multiple Refund Methods Displayed', {
        addToScopeOnce: { returnSummary }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = useCallback(
    (method: TReturnMethod) => {
      handleChange(method.value);
      trackChosenReturnMethod(method.name);
    },
    [handleChange, trackChosenReturnMethod]
  );

  if (!returnSummary) return null;

  const {
    currencyUnit,
    returnSiteCreditBonus,
    returnSiteCreditBonusAmount,
    returnSiteCreditBonusPercentage
  } = returnSummary.attributes;

  return (
    <>
      <div className="RequestReturnMethod">
        <Stack space={4}>
          <Text treatment="h5">Refund Method</Text>

          <Stack space={3}>
            {returnSummary.attributes.returnMethods.map(method =>
              returnSiteCreditBonus ? (
                <Box key={method.value} onClick={() => handleClick(method)}>
                  <Stack
                    alignItems="center"
                    direction="horizontal"
                    justifyContent="space-between"
                    space={2}
                  >
                    <Stack direction="horizontal" space={3}>
                      <RadioButton
                        data-testid={`refund-method-${method.name}`}
                        checked={returnSummary.attributes.returnMethodId === method.value}
                        onChange={() => handleClick(method)}
                      />

                      <Stack space={2}>
                        {method.name}

                        {method.value === ReturnMethod.SiteCredit &&
                          returnSiteCreditBonusPercentage > 0 &&
                          Boolean(returnSiteCreditBonusAmount) && (
                            <Text treatment="bold1">
                              {returnSiteCreditBonusPercentage}% Back Bonus
                            </Text>
                          )}
                      </Stack>
                    </Stack>

                    {method.value === ReturnMethod.SiteCredit &&
                      Number(returnSiteCreditBonusAmount) > 0 && (
                        <Text treatment="bold1">
                          {`${currencyUnit}${returnSiteCreditBonusAmount} Bonus`}
                        </Text>
                      )}
                  </Stack>
                </Box>
              ) : (
                <RadioButton
                  data-testid={`refund-method-${method.name}`}
                  key={method.value}
                  checked={returnSummary.attributes.returnMethodId === method.value}
                  onChange={() => handleClick(method)}
                >
                  {method.name}
                </RadioButton>
              )
            )}
          </Stack>

          <RequestReturnMethodGuestUserFeatures
            numberOfAvailableReturnMethods={returnSummary.attributes.returnMethods.length}
            selectedReturnMethodId={returnSummary.attributes.returnMethodId}
          />
        </Stack>
      </div>

      <Overlay show={loading}>
        <LoadingBalls />

        <Text treatment="eyebrow">Changing</Text>
      </Overlay>
    </>
  );
};
